import { useState } from 'react';
import { hotList } from '@/mocks/ranking';
import { fmtCount } from '@/lib/format';
import SectionHeading from './SectionHeading';

const rankBadge = (i: number) => {
  if (i === 0) return 'bg-accent-500 text-background-50';
  if (i === 1) return 'bg-secondary-200 text-secondary-900';
  if (i === 2) return 'bg-primary-200 text-primary-800';
  return 'bg-background-200 text-foreground-600';
};

export default function RankingSection() {
  const [extra, setExtra] = useState<Record<number, number>>({});

  const vote = (id: number) => setExtra((e) => ({ ...e, [id]: (e[id] || 0) + 1 }));
  const count = (id: number, base: number) => base + (extra[id] || 0);

  const featured = hotList[0];
  const rest = hotList.slice(1);

  return (
    <section id="hotlist" className="scroll-mt-20 bg-background-50 py-14 md:py-20">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-6 lg:px-10">
        <SectionHeading
          eyebrow="全站热议 · 票数实时累加"
          title="实时热榜"
          desc="顶流、塌房、名场面、萌宠……全网最热的瓜都在这里排队上榜。看到想支持的，点一下「顶」为它加一票。"
        />

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1fr_320px]">
          {/* Main list */}
          <div>
            {/* Featured #1 */}
            <article className="relative overflow-hidden rounded-3xl border border-background-200 bg-gradient-to-br from-background-50 to-accent-100/50 p-6 md:p-7">
              <div className="flex flex-col gap-5 sm:flex-row sm:items-center">
                <div className="flex shrink-0 items-center gap-3">
                  <span className="flex h-16 w-16 items-center justify-center rounded-2xl bg-accent-500 font-display text-3xl text-background-50">
                    1
                  </span>
                  <div className="sm:hidden">
                    <span className="inline-flex rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-600">{featured.tag}</span>
                  </div>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="hidden items-center gap-2 sm:flex">
                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded-full bg-accent-100 px-2.5 py-1 text-xs font-medium text-accent-800">
                      <i className="ri-vip-crown-fill text-sm leading-none" />
                      {featured.tag}
                    </span>
                    <span className="whitespace-nowrap rounded-full bg-background-100 px-2.5 py-1 text-xs text-foreground-500">今日最热</span>
                  </div>
                  <h3 className="mt-1.5 text-xl font-bold leading-snug text-foreground-950 md:text-2xl">{featured.title}</h3>
                  <p className="mt-1 text-sm text-foreground-500">
                    实时人气 <b className="font-display text-2xl text-primary-600">{fmtCount(count(featured.id, featured.votes))}</b> 票 · 热度指数 {featured.hot}
                  </p>
                </div>
                <div className="flex shrink-0 flex-col items-start gap-2 sm:items-end">
                  <span className="inline-flex items-center gap-1 rounded-full bg-primary-100 px-3 py-1 text-xs font-bold text-primary-700">
                    <i className="ri-arrow-up-line text-sm leading-none" />
                    {featured.delta}
                  </span>
                  <button
                    onClick={() => vote(featured.id)}
                    className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600"
                  >
                    <i className="ri-fire-fill text-base leading-none" />
                    顶一下
                  </button>
                </div>
              </div>
            </article>

            {/* Rest items grid */}
            <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
              {rest.map((item, idx) => (
                <article
                  key={item.id}
                  className="group flex items-center gap-3 rounded-2xl border border-background-200 bg-background-100/60 p-4 transition-colors hover:border-background-300 hover:bg-background-100"
                >
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-xl font-display text-xl ${rankBadge(idx + 1)}`}>
                    {idx + 2}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="max-w-[8rem] truncate whitespace-nowrap rounded-full bg-background-50 px-2 py-0.5 text-[11px] text-foreground-500">{item.tag}</span>
                      <span
                        className={`inline-flex items-center gap-0.5 whitespace-nowrap text-[11px] font-semibold ${
                          item.trend === 'down' ? 'text-secondary-600' : 'text-primary-600'
                        }`}
                      >
                        <i className={`${item.trend === 'down' ? 'ri-arrow-down-line' : 'ri-arrow-up-line'} text-xs leading-none`} />
                        {item.delta}
                      </span>
                    </div>
                    <h4 className="mt-1.5 line-clamp-2 text-sm font-semibold leading-snug text-foreground-900 transition-colors group-hover:text-primary-700">
                      {item.title}
                    </h4>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <span className="text-xs text-foreground-500">
                        <i className="ri-heart-3-fill mr-1 text-primary-500" />
                        {fmtCount(count(item.id, item.votes))}票
                      </span>
                      <button
                        onClick={() => vote(item.id)}
                        className="inline-flex items-center gap-1 whitespace-nowrap rounded-full border border-background-300 bg-background-50 px-2.5 py-1 text-xs font-medium text-foreground-700 transition-colors hover:border-primary-300 hover:bg-primary-100 hover:text-primary-700"
                      >
                        <i className="ri-thumb-up-line text-xs leading-none" />
                        顶
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>

          {/* Sidebar */}
          <aside className="space-y-5">
            <div className="rounded-3xl border border-secondary-200 bg-secondary-100/60 p-6">
              <h4 className="flex items-center gap-2 font-display text-lg text-secondary-900">
                <i className="ri-shield-check-line text-xl leading-none" />
                透明计票
              </h4>
              <ul className="mt-4 space-y-3 text-sm text-secondary-900">
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-xs leading-none" />
                  </span>
                  每票实时累加，刷新即可看到最新名次。
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-xs leading-none" />
                  </span>
                  结果由全网网友共同票选，公平看得见。
                </li>
                <li className="flex items-start gap-2.5">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-secondary-500 text-background-50">
                    <i className="ri-check-line text-xs leading-none" />
                  </span>
                  本榜单仅供娱乐讨论，不以票数评判个人。
                </li>
              </ul>
            </div>

            <div className="rounded-3xl border border-background-200 bg-accent-100/60 p-6">
              <h4 className="flex items-center gap-2 font-display text-lg text-accent-800">
                <i className="ri-arrow-up-circle-fill text-xl leading-none" />
                上升最快
              </h4>
              <ul className="mt-4 space-y-3">
                {hotList.slice(0, 3).map((it) => (
                  <li key={it.id} className="flex items-center gap-3">
                    <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-background-50 text-sm text-primary-600">
                      <i className="ri-arrow-up-line text-base leading-none" />
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-medium text-foreground-900">{it.title}</p>
                      <p className="text-xs text-foreground-500">
                        <b className="text-primary-600">{it.delta}</b> · 热度 {it.hot}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}